import React, { PropsWithChildren } from 'react'
import clsx from 'clsx'
import {
  MenuTreeHierarchyProduct,
  OrderFormat,
  OutletListItem,
} from '@ancon/wildcat-types'
import isMenuTreeProductOutOfStock from '@ancon/wildcat-utils/inventory/isMenuTreeProductOutOfStock'
import useTranslation from 'next-translate/useTranslation'
import getFormattedCurrency from '@ancon/wildcat-utils/currency/getFormattedCurrency'
import isMenuTreeHierarchyProductAvailable from '@ancon/wildcat-utils/outlet/isMenuTreeHierarchyProductAvailable'

import Card from '../../layout/components/card/Card'
import BodyText from '../../app/components/BodyText'
import isDefaultProductCardStyle from '../utils/isDefaultProductCardStyle'

import ProductName from './ProductName'
import ProductCardBanner from './ProductCardBanner'
import ProductCardAvailabilityText from './ProductCardAvailabilityText'
import styles from './ProductCard.module.scss'

export type ProductCardProps = PropsWithChildren<{
  product: MenuTreeHierarchyProduct
  onClick?: () => void
  selectedOrderFormat: OrderFormat
  outletOrderFormats: OutletListItem['supportedOrderFormats']
  enlargeOnHover?: boolean
  highlighted?: boolean
}>

export default function ProductCard({
  product,
  onClick,
  selectedOrderFormat,
  outletOrderFormats,
  enlargeOnHover = false,
  highlighted = false,
  children,
}: ProductCardProps) {
  const { t } = useTranslation('common')

  const {
    productName,
    productDescription,
    imageId,
    orderFormat,
    mainVariantPrice,
    productStockCounts,
  } = product

  const {
    unitPrice,
    unitPriceCurrency,
    showPricePresetLabel,
    pricePresetName,
  } = mainVariantPrice

  const defaultProductCardStyle = isDefaultProductCardStyle()

  const isOutOfStock = isMenuTreeProductOutOfStock(productStockCounts)

  const isProductUnavailable = !isMenuTreeHierarchyProductAvailable(
    product,
    selectedOrderFormat,
  )

  const disabled = isProductUnavailable || isOutOfStock

  return (
    <Card
      tabIndex={0}
      className={clsx(styles.container, {
        [styles.imageOnTopStyle]: !defaultProductCardStyle,
        [styles.enlargeOnHover]: enlargeOnHover,
        [styles.highlighted]: !!highlighted,
        [styles.disabled]: disabled,
      })}
      onClick={onClick}
      disabled={disabled}
      aria-label={productName ?? undefined}
    >
      <div
        className={clsx(styles.infoContainer, {
          [styles.disabled]: disabled,
        })}
      >
        <ProductName as="h3" className={styles.productName}>
          {productName}
        </ProductName>
        <div
          className={clsx(styles.productMetaContainer, {
            [styles.imageOnTopStyle]: !defaultProductCardStyle,
          })}
        >
          <div className={styles.priceContainer}>
            <div className={styles.leftContainer}>
              <BodyText className={styles.productPrice}>
                {getFormattedCurrency(unitPrice, unitPriceCurrency)}
              </BodyText>
              {showPricePresetLabel && pricePresetName && (
                <BodyText className={styles.pricePresetTag}>
                  {pricePresetName}
                </BodyText>
              )}
              {isOutOfStock && (
                <BodyText className={styles.outOfStockTag}>
                  {t('components.productCard.outOfStock')}
                </BodyText>
              )}
            </div>
          </div>
          <div className={styles.spaceBetween} />
          {!!productDescription && !disabled && (
            <BodyText className={styles.productDescription}>
              {productDescription}
            </BodyText>
          )}
          {isProductUnavailable && !isOutOfStock && (
            <ProductCardAvailabilityText
              productOrderFormat={orderFormat}
              outletOrderFormats={outletOrderFormats}
            />
          )}
        </div>
      </div>
      <ProductCardBanner
        productName={productName}
        productImageId={imageId}
        disabled={disabled}
      />
      {children}
    </Card>
  )
}
